/* Pływający przycisk koszyka.

   Stan koszyka bierzemy ze zdarzenia cartUpdate, które publikują wszystkie
   ścieżki dodawania i zmiany (nasz konfigurator, cart.js z Dawn, quick add).
   Ładunek zdarzenia ma jednak dwa kształty: przy zmianie ilości przychodzi
   pełny koszyk z item_count, przy dodaniu — sam dodany wiersz. W tym drugim
   przypadku dopytujemy /cart.js, zamiast zgadywać licznik. */

class BjFloatingCart extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bj-fab-button]');
    this.label = this.querySelector('[data-bj-fab-label]');
    this.icon = this.querySelector('[data-bj-fab-icon]');
    if (!this.button) return;

    this.count = parseInt(this.dataset.count, 10) || 0;
    this.threshold = parseInt(this.dataset.threshold, 10) || 700;
    this.emptyMode = this.dataset.emptyMode || 'cta';
    this.ctaTarget = this.dataset.ctaTarget || '#produkt';
    this.ctaLabel = this.label ? this.label.textContent : 'Zamów';

    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);

    this.button.addEventListener('click', this.onClick);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    // Powrót „wstecz” przywraca pozycję przewijania po connectedCallback,
    // więc bez tego przycisk czekałby ukryty do pierwszego ruchu myszy.
    this.onPageShow = () => this.render();
    window.addEventListener('pageshow', this.onPageShow);

    if (typeof subscribe === 'function') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    }

    // Szuflada koszyka zasłania przycisk — chowamy go, gdy jest otwarta.
    this.drawer = document.querySelector('cart-drawer');
    if (this.drawer && 'MutationObserver' in window) {
      this.observer = new MutationObserver(() => this.render());
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }

    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('pageshow', this.onPageShow);
    this.button?.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
    this.unsubscribe?.();
  }

  async onCartUpdate(event) {
    const data = event?.cartData;
    if (data && typeof data.item_count === 'number') {
      this.count = data.item_count;
    } else {
      try {
        const cart = await fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
        this.count = cart.item_count;
      } catch {
        // Licznik zostaje na ostatniej znanej wartości — lepsze niż wyzerowanie.
        return;
      }
    }
    this.render();
  }

  onScroll() {
    // Malowanie zbijamy do jednej klatki, scroll potrafi sypać setkami zdarzeń.
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.render();
    });
  }

  get drawerOpen() {
    return this.drawer?.classList.contains('active') === true;
  }

  onClick(event) {
    if (this.count === 0) return; // pusty koszyk: zwykły link do konfiguratora

    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return; // bez szuflady: przejście na /cart

    event.preventDefault();
    drawer.setActiveElement?.(this.button);
    drawer.open();
  }

  render() {
    const filled = this.count > 0;

    if (this.label) this.label.textContent = filled ? this.count + ' w koszyku' : this.ctaLabel;
    if (this.icon) this.icon.hidden = !filled;

    this.button.setAttribute('href', filled ? window.routes.cart_url : this.ctaTarget);
    this.button.setAttribute(
      'aria-label',
      filled ? `Otwórz koszyk, produktów: ${this.count}` : this.ctaLabel
    );

    const scrolled = window.scrollY > this.threshold;
    const allowed = filled || this.emptyMode === 'cta';
    this.hidden = !(scrolled && allowed) || this.drawerOpen;
  }
}

if (!customElements.get('bj-floating-cart')) {
  customElements.define('bj-floating-cart', BjFloatingCart);
}
